import { Grid } from '@mui/material';
import { GridColDef, DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getJobsPostedOnJobBoards } from 'wisestep-api';
import { WsColors, WsTypography, WsTypoVariants, Item, getDateUsingFormat } from 'wisestep-base';

import { getSumOfColumnRecords } from '../../../composableAnalytics/BaseCharts/clientListing/helper/payloadsAndTypesHelper';
import { IAggregation } from '../../types';
import { collectUniqueDatesFrom2Levels } from '../../utils/InternalMISAnalyticsHelper';
import { IMISAnalyticsQuery } from '../IMISAnalyticsQuery';

const JobsPostedOnJobBoardsTable = ({ query, duration }: IMISAnalyticsQuery) => {
  const paginationSize = 10;
  const [aggregatedData, setAggregatedData] = useState<IAggregation[]>([]);
  const [uniqueDates, setUniqueDates] = useState<string[]>([]);

  const dataMap: Record<string, Record<string, Record<string, number>>> = {};

  useEffect(() => {
    const fetchData = async () => {
      const response = await getJobsPostedOnJobBoards(query);
      setAggregatedData(response.data.aggregations.aggregation.buckets);
      setUniqueDates(collectUniqueDatesFrom2Levels(response.data.aggregations.aggregation.buckets));
    };
    fetchData();
  }, [query]);

  aggregatedData.forEach((outerBucket) => {
    const workspace = outerBucket.key;
    dataMap[workspace] = {};

    outerBucket.aggregation?.buckets?.forEach((innerBucket) => {
      const source = innerBucket.key;

      innerBucket.aggregation?.buckets?.forEach((bucket) => {
        const date = bucket?.key_as_string ?? '';

        if (!dataMap[workspace][source]) {
          dataMap[workspace][source] = {};
        }

        dataMap[workspace][source][date] = bucket.doc_count ?? 0;
      });
    });
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    {
      field: 'workspace',
      headerName: query.loadAllWorkspacesData ? 'Workspace' : 'Created By',
      flex: 1,
      minWidth: 200,
    },
    { field: 'jobBoard', headerName: 'Job Board', flex: 1, minWidth: 150 },
    ...uniqueDates.flatMap((date) => ({
      field: date,
      headerName: getDateUsingFormat(date, 'DD MMM'),
      type: 'number',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => {
        const workspace = params.row.workspace;
        const jobBoard = params.row.jobBoard;
        return dataMap[workspace]?.[jobBoard]?.[date] ?? 0;
      },
    })),
  ];

  const tableData: Record<string, unknown>[] = [];

  Object.keys(dataMap).forEach((workspace) => {
    const workspaceData = dataMap[workspace];
    Object.keys(workspaceData).forEach((jobBoard) => {
      const rowData: Record<string, number | string> = {
        id: uuidv4(),
        workspace,
        jobBoard,
      };
      uniqueDates.forEach((date) => {
        rowData[date] = dataMap[workspace]?.[jobBoard]?.[date] ?? 0;
      });
      tableData.push(rowData);
    });
  });

  return (
    <Grid item xs={12} bgcolor={WsColors.White}>
      <Item>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
          {`Jobs Posted to External Job boards by Workspace ${
            duration ?? ' Last 8 weeks - Weekly'
          }`}
        </WsTypography>
      </Item>
      <Item>
        <DataGrid
          rows={tableData}
          columns={getSumOfColumnRecords(tableData, columns, ['id', 'workspace', 'jobBoard'])}
          components={{ Toolbar: GridToolbar }}
          pageSize={paginationSize}
          rowsPerPageOptions={[paginationSize]}
          autoHeight={true}
        />
      </Item>
    </Grid>
  );
};
export default JobsPostedOnJobBoardsTable;
